import { faChevronDown, faCube } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext, useWatch } from "react-hook-form";

import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { DropdownItem } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { entityIcons } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { ColumnType } from "src/featureCatalogue/Queries/Create/helpers";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const NO_ENTITY_KEY = "__no_entity__";

type DetectedEntitySectionProps = {
  readOnly: boolean;
  isLoading: boolean;
  isEditMode: boolean;
  columnTypes: Record<string, ColumnType>;
};

export const DetectedEntitySection: React.FC<DetectedEntitySectionProps> = ({
  readOnly,
  isLoading,
  isEditMode,
  columnTypes,
}) => {
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const { setValue, control } = useFormContext<QueryFormValues>();
  const detectedEntity = useWatch({
    control,
    name: "columns.entities.0.entity_name",
  });

  const isNoEntity = !detectedEntity;
  const columnCount = Object.keys(columnTypes).length;
  const isSingleColumn = columnCount === 1;
  const isEntityChangeDisabled = readOnly || isEditMode || isSingleColumn;

  const getDisabledReason = (): string | undefined => {
    if (isEditMode) return "Entity cannot be changed after a Query is created";
    if (isSingleColumn)
      return "Single Column Queries create Global Features. Add more Columns to assign an Entity.";
    return undefined;
  };

  const disabledReason = getDisabledReason();

  const selectedSchema = entitySchemas?.entities.find(
    (e) => e._id === detectedEntity,
  );

  const selectedIcon = selectedSchema
    ? (entityIcons[selectedSchema._display_symbol] ?? faCube)
    : faCube;

  const selectedDisplayName = isNoEntity
    ? "No entity"
    : (selectedSchema?._display_name_singular ?? detectedEntity);

  const iconByEntityId = Object.fromEntries(
    (entitySchemas?.entities ?? []).map((entity) => [
      entity._id,
      entityIcons[entity._display_symbol] ?? faCube,
    ]),
  );

  const entityOptions = (entitySchemas?.entities ?? []).map((entity) => ({
    key: entity._id,
    value: entity._display_name_singular,
  }));

  const options = [
    ...entityOptions,
    { key: NO_ENTITY_KEY, value: "No entity" },
  ];

  const handleSelect = (key: string | null) => {
    setValue(
      "columns.entities.0.entity_name",
      key === NO_ENTITY_KEY ? "" : (key ?? ""),
    );
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-3 p-4"
        data-loc="detected-entity-section"
      >
        <Skeleton height="h-5" width="w-32" />
        <Skeleton height="h-5" width="w-17" />
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-4"
      data-loc="detected-entity-section"
    >
      <span className="font-inter-semibold-13px text-gray-800">
        {isEditMode ? "Entity:" : "Detected Entity:"}
      </span>
      <FixedPositionedDropdown
        buttonClassName="!outline-none !ring-0 !border-0 focus:!outline-none focus:!ring-0 focus:!border-0 focus-visible:!outline-none"
        buttonDataLoc="detected-entity-dropdown-button"
        dataLoc="detected-entity-dropdown"
        // We don't allow changing the entity in edit mode
        disabled={isEntityChangeDisabled}
        elements={options}
        renderButtonValue={() => (
          <Tooltip disabled={!disabledReason} title={disabledReason} asChild>
            <div className="flex max-w-40 justify-between rounded border border-gray-200 bg-gray-200 px-1">
              <Icon icon={selectedIcon} size="xs" />
              <span className="mx-1 min-w-0 truncate font-inter-medium-12px">
                {selectedDisplayName}
              </span>
              {!isEntityChangeDisabled && (
                <Icon icon={faChevronDown} size="2xs" />
              )}
            </div>
          </Tooltip>
        )}
        renderValue={({ key, value, selected }) => (
          <DropdownItem data-selected={selected || undefined}>
            <div className="flex items-center gap-2">
              <Icon
                icon={
                  key === NO_ENTITY_KEY
                    ? faCube
                    : (iconByEntityId[key ?? ""] ?? faCube)
                }
                size="xs"
                variant="default"
              />
              {value}
            </div>
          </DropdownItem>
        )}
        selected={isNoEntity ? NO_ENTITY_KEY : detectedEntity}
        onSelect={handleSelect}
      />
    </div>
  );
};
